import {
  ProductLevelCreditResult,
  Subscription,
  StoreSubscription,
  TermEstimate,
  SubscriptionTerm
} from '@futrli-sdk/billing';
import { Dinero } from '@futrli/localisation';
import { appAuthenticator, ProductSlug, AuthenticatorUser } from '@futrli/shared-services';
import { get } from 'lodash';

import { PaymentFormInfo } from '../billing-forms/types';
import { fetchProductSetup, fetchWorkspaceById } from '../core/api';
import { YEAR_MONTHS } from '../core/constants';

/**
 * Pulls down a given product setup and returns the `is_pro` boolean.
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} productSetupId
 * @returns {Promise<boolean>}
 */
export const isProductPaid = async (
  workspaceId: string,
  organisationId: string,
  productSetupId: string
): Promise<boolean> => {
  const productSetup = await fetchProductSetup(workspaceId, organisationId, productSetupId);

  return productSetup.is_pro;
};

/**
 * Get a workspace and check if the current user is the owner.
 * @param {string} workspaceId
 * @returns {boolean}
 */
export const isWorkspaceOwner = async (workspaceId: string): Promise<boolean> => {
  const { owner } = await fetchWorkspaceById(workspaceId);
  const user: AuthenticatorUser = await appAuthenticator.getCurrentAuthenticatedUser();

  return user?.attributes?.sub === owner.id;
};

/**
 * Determines if a user has any remaining license to activate the product.
 * @param {ProductLevelCreditResult|null} licenses
 * @returns {boolean}
 */
export const hasLicensesToActivateProduct = (licenses: ProductLevelCreditResult | null): boolean => {
  return !licenses ? false : licenses.remaining > 0;
};

/**
 * Calculates the monthly amount from an annual one.
 * @param {number} annualAmount
 * @returns {number}
 */
export const getMonthlyAmount = (annualAmount: number) => {
  return Dinero({ amount: annualAmount }).divide(YEAR_MONTHS).getAmount();
};

/**
 * Calculates the annual discount
 * @param {TermEstimate[]} plans
 * @returns {number}
 */
export const calculateAnnualDiscount = (plans: TermEstimate[]): number => {
  const annual = plans.find(plan => plan.term === SubscriptionTerm.annual);
  const monthly = plans.find(plan => plan.term === SubscriptionTerm.monthly);

  if (!annual || !monthly) {
    return 0;
  }

  return Math.round((1 - Number(annual.estimated_cost) / (Number(monthly.estimated_cost) * YEAR_MONTHS)) * 100);
};

/**
 * Formats the term string to be rendered
 * @param {string} term
 * @return {string}
 */
export function formatTermString(term: string): string {
  return term === SubscriptionTerm.annual ? 'annually' : SubscriptionTerm.monthly;
}

/**
 * Formats the subscription data coming from the DynamoDB and the estimate product cost
 * into the PaymentFormInfo format.
 *
 * @param {StoreSubscription} subscriptionData
 * @param {TermEstimate} productEstimate
 * @returns {PaymentFormInfo}
 */
export function getPaymentInfoFromStoredDBData(
  subscriptionData: StoreSubscription,
  productEstimate: TermEstimate
): PaymentFormInfo {
  // At the moment the internal sales portal does not support a customer being sold
  // more than one product, so we are getting just one product.
  const signUpProduct = subscriptionData.products[0];

  return {
    totalCharges: productEstimate.estimated_cost,
    productSlug: signUpProduct.name.toLowerCase() as ProductSlug,
    integrationLevel: signUpProduct.level,
    subscription: {
      creationDate: subscriptionData.creation_date,
      term: subscriptionData.term,
      currencyCode: subscriptionData.currency_code,
      addons: signUpProduct.quantity,
      coupons: subscriptionData.coupon_ids,
      plan_type: subscriptionData.plan_type
    },
    hasExistingCharges: productEstimate.estimated_cost > 0
  };
}

/**
 * Determines if there are existing charges or not.
 * @param {PaymentFormInfo|null} paymentInfo
 * @returns {boolean}
 */
export function hasExistingCharges(paymentInfo: PaymentFormInfo | null): boolean {
  return get(paymentInfo, 'hasExistingCharges', false);
}

/**
 * Gets the subscription id.
 * @param {Subscription} subscription
 * @returns {string}
 */
export function getSubscriptionId(subscription: Subscription): string {
  return `${subscription.term}-${subscription.currency_code.toLowerCase()}`;
}
